import { doc, getDoc, setDoc, serverTimestamp } from "./fs";
import { db } from "../config/firebase";
import { AI_CONFIG_DOC, firstSnapshot, loadSummaries, normalizeAiConfig } from "./aiAnalysis";

/** config/aiAnalysis: { enabled, provider, updatedAt, updatedBy }. Readable by everyone, writable by Full Admin only. */
export async function loadAiConfig() {
  try {
    const snap = await getDoc(doc(db, ...AI_CONFIG_DOC));
    return normalizeAiConfig(snap.exists() ? snap.data() : null);
  } catch {
    return normalizeAiConfig(null);
  }
}

export async function saveAiConfig({ enabled, provider = null }, uid) {
  const next = normalizeAiConfig({ enabled: enabled === true, provider });
  await setDoc(
    doc(db, ...AI_CONFIG_DOC),
    { ...next, updatedAt: serverTimestamp(), updatedBy: uid || null },
    { merge: true }
  );
  return next;
}

/** Dataset loaders; services are imported lazily so the panel stays light on every page. */
export function webLoaders(uid) {
  return {
    invoices: async () => (await import("./firestore")).InvoiceService.getInvoices(uid),
    accounts: async () => (await import("./firestore")).AccountService.getAccounts(uid),
    approvals: async () => (await import("./firestore")).ApprovalService.getApprovals(uid),
    products: async () => (await import("./firestore")).ProductService.getProducts(uid),
    stockMovements: async () => (await import("./firestore")).StockMovementService.getAllMovements(),
    files: async () => (await import("./firestore")).FileService.getFiles(uid),
    warehouses: async () => (await import("./inventoryService")).WarehouseService.list(),
    suppliers: async () => (await import("./procurementService")).SupplierService.list(),
    purchaseOrders: async () => (await import("./procurementService")).PurchaseOrderService.list(),
    rfqs: async () => (await import("./procurementService")).RfqService.list(),
    customers: async () => (await import("./salesService")).CustomerService.list(),
    salesOrders: async () => (await import("./salesService")).SalesOrderService.list(),
    productionPlans: async () => (await import("./manufacturingService")).ProductionPlanService.list(),
    manufacturingOrders: async () => (await import("./manufacturingService")).ManufacturingOrderService.list(),
    inspections: async () => (await import("./qualityService")).InspectionService.list(),
    nonConformances: async () => (await import("./qualityService")).NonConformanceService.list(),
    exportOrders: async () => (await import("./internationalService")).ExportOrderService.list(),
    internationalShipments: async () => (await import("./internationalService")).InternationalShipmentService.list(),
    customsDeclarations: async () => (await import("./internationalService")).CustomsDeclarationService.list(),
    returns: async () => (await import("./afterSalesService")).ReturnService.list(),
    rmas: async () => (await import("./afterSalesService")).RmaService.list(),
    warranties: async () => (await import("./afterSalesService")).WarrantyService.listWarranties(),
    serviceOrders: async () => (await import("./afterSalesService")).ServiceOrderService.list(),
    supportTickets: async () => {
      const { subscribeTickets } = await import("./supportTickets");
      return firstSnapshot((onData, onError) => subscribeTickets(uid, "mine", onData, onError));
    }
  };
}

const CACHE_MS = 5 * 60 * 1000;
const cache = new Map();

/** Summaries for the given datasets, cached per user for a few minutes so reopening the panel is instant. */
export async function loadWebSummaries(datasetIds, uid, { force = false } = {}) {
  const now = Date.now();
  const out = { summaries: {}, failed: [] };
  const missing = [];
  for (const id of datasetIds) {
    const hit = cache.get(`${uid}:${id}`);
    if (!force && hit && now - hit.at < CACHE_MS) out.summaries[id] = hit.summary;
    else missing.push(id);
  }
  if (missing.length) {
    const res = await loadSummaries(missing, webLoaders(uid), { now });
    for (const [id, summary] of Object.entries(res.summaries)) {
      cache.set(`${uid}:${id}`, { at: now, summary });
      out.summaries[id] = summary;
    }
    out.failed = res.failed;
  }
  return out;
}

export function clearAiSummaryCache() {
  cache.clear();
}
